import { useEffect, useState, type ReactNode } from 'react';
import { Icon, Uncited } from '@garnet/ui';
import type { Clause, Condition, Factor, Rule, RuleTest, StudioOutputType } from '../data/types.js';
import { OUTPUT_OPTIONS, type StudioContext } from '../studio.js';

/** Strip the always-present abstain clause so the author edits only the matching rows. */
function initDraft(o: Rule): Rule {
  const d = JSON.parse(JSON.stringify(o)) as Rule;
  if (d.condition.kind === 'conditions') {
    d.condition.clauses = d.condition.clauses.filter((c) => !/abstain/i.test(c.then));
  }
  return d;
}

/** Re-attach the immovable "otherwise → abstain" clause: nothing matches, nothing is guessed. */
function finalizeDraft(d: Rule): Rule {
  const out = JSON.parse(JSON.stringify(d)) as Rule;
  if (out.condition.kind === 'conditions') {
    out.condition.clauses = [
      ...out.condition.clauses.filter((c) => !/abstain/i.test(c.then)),
      { when: 'Any other case', then: 'Abstain — no rule applies' },
    ];
  }
  return out;
}

function REField({
  label,
  hint,
  children,
  wide,
}: {
  label: ReactNode;
  hint?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span className="field-label" style={{ margin: 0 }}>
        {label}
        {hint && (
          <span className="dimtext" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {' '}
            · {hint}
          </span>
        )}
      </span>
      {children}
    </div>
  );
}

function CitationPicker({
  ctx,
  citations,
  setCitations,
}: {
  ctx: StudioContext;
  citations: string[];
  setCitations: (c: string[]) => void;
}) {
  const { api } = ctx;
  const [adding, setAdding] = useState(false);
  const avail = api.sources().filter((s) => !citations.includes(s.id));
  return (
    <div>
      {citations.length === 0 ? (
        <div style={{ marginBottom: 10 }}>
          <Uncited />
          <p className="metatext" style={{ marginTop: 8 }}>
            Not cited yet. A rule can’t leave draft without a source.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>
          {citations.map((cid) => {
            const s = api.source(cid);
            if (!s) return null;
            return (
              <div
                key={cid}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '8px 10px',
                  background: 'var(--bg-sunken)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    className="mono cite-link"
                    style={{ fontSize: 12 }}
                    role="button"
                    onClick={() => ctx.openSource(cid)}
                  >
                    {s.citation}
                  </div>
                  <div className="metatext" style={{ marginTop: 2 }}>
                    {s.title} ·{' '}
                    <span className="tag" style={{ padding: '0 4px' }}>
                      {s.kind}
                    </span>
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCitations(citations.filter((c) => c !== cid))}
                  title="remove"
                >
                  <Icon name="x" size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {adding ? (
        <select
          className="inp mono"
          autoFocus
          style={{ fontSize: 12 }}
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) {
              setCitations([...citations, e.target.value]);
              setAdding(false);
            }
          }}
        >
          <option value="" disabled>
            choose a source of law…
          </option>
          {avail.map((s) => (
            <option key={s.id} value={s.id}>
              {s.citation}
            </option>
          ))}
        </select>
      ) : (
        <button className="btn btn-sm mono" onClick={() => setAdding(true)}>
          <Icon name="plus" size={12} />
          Cite a source
        </button>
      )}
    </div>
  );
}

export function RuleEditor({
  ctx,
  ruleId,
  onSaved,
  onCancel,
}: {
  ctx: StudioContext;
  ruleId: string;
  onSaved: (id: string) => void;
  onCancel: (deleted: boolean) => void;
}) {
  const { api } = ctx;
  const orig = api.rule(ruleId);
  const [d, setD] = useState<Rule>(() => initDraft(orig ?? ({} as Rule)));
  const [meta] = useState(() => ({
    id: orig?.ruleId ?? ruleId,
    wasPublished: orig?.status === 'published',
    isNew: !!orig?.isNew,
  }));
  useEffect(() => {
    const o = api.rule(ruleId);
    if (o) setD(initDraft(o));
  }, [ruleId, api]);
  if (!orig) return <div className="metatext">Rule not found.</div>;

  const set = (patch: Partial<Rule>) => setD((p) => ({ ...p, ...patch }));
  const setOut = (patch: Partial<Rule['output']>) => setD((p) => ({ ...p, output: { ...p.output, ...patch } }));

  // factors (both condition kinds carry them)
  const setFactors = (fn: (f: Factor[]) => Factor[]) =>
    setD((p) => {
      const c = p.condition;
      return { ...p, condition: { ...c, factors: fn(c.factors) } } as Rule;
    });
  const addFactor = () => setFactors((f) => [...f, { name: '', note: '' }]);
  const setFactor = (i: number, patch: Partial<Factor>) =>
    setFactors((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delFactor = (i: number) => setFactors((f) => f.filter((_, j) => j !== i));

  // clauses (conditions kind)
  const setClauses = (fn: (c: Clause[]) => Clause[]) =>
    setD((p) => {
      const c = p.condition;
      if (c.kind !== 'conditions') return p;
      return { ...p, condition: { ...c, clauses: fn(c.clauses) } };
    });
  const addClause = () => setClauses((c) => [...c, { when: '', then: '' }]);
  const setClause = (i: number, patch: Partial<Clause>) =>
    setClauses((c) => c.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delClause = (i: number) => setClauses((c) => c.filter((_, j) => j !== i));

  // table kind
  const setColumn = (ci: number, val: string) =>
    setD((p) => {
      const c = p.condition;
      if (c.kind !== 'table') return p;
      return { ...p, condition: { ...c, columns: c.columns.map((x, k) => (k === ci ? val : x)) } };
    });
  const setCell = (ri: number, ci: number, val: string) =>
    setD((p) => {
      const c = p.condition;
      if (c.kind !== 'table') return p;
      return {
        ...p,
        condition: { ...c, rows: c.rows.map((r, j) => (j === ri ? r.map((x, k) => (k === ci ? val : x)) : r)) },
      };
    });
  const addRow = () =>
    setD((p) => {
      const c = p.condition;
      if (c.kind !== 'table') return p;
      return { ...p, condition: { ...c, rows: [...c.rows, c.columns.map(() => '')] } };
    });
  const delRow = (ri: number) =>
    setD((p) => {
      const c = p.condition;
      if (c.kind !== 'table') return p;
      return { ...p, condition: { ...c, rows: c.rows.filter((_, j) => j !== ri) } };
    });

  const switchKind = (kind: Condition['kind']) =>
    setD((p) => {
      if (p.condition.kind === kind) return p;
      if (kind === 'table')
        return {
          ...p,
          condition: { kind: 'table', factors: p.condition.factors, columns: ['When this is true', 'Then'], rows: [['', '']] },
        };
      return { ...p, condition: { kind: 'conditions', factors: p.condition.factors, clauses: [{ when: '', then: '' }] } };
    });

  // tests
  const setTests = (fn: (t: RuleTest[]) => RuleTest[]) => set({ tests: fn(d.tests) });
  const addTest = () => setTests((t) => [...t, { input: '', expect: '', status: 'pending' }]);
  const setTest = (i: number, patch: Partial<RuleTest>) =>
    setTests((t) => t.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delTest = (i: number) => setTests((t) => t.filter((_, j) => j !== i));
  const markChecked = () => setTests((t) => t.map((x) => ({ ...x, status: 'pass' })));

  const cited = d.citations.length > 0;
  const testsPass = d.tests.length > 0 && d.tests.every((t) => t.status === 'pass');
  const meetsBar = cited && testsPass;
  const canPublishRole = ctx.can('publish');
  const canPublish = meetsBar && canPublishRole;

  const finalizeAnd = (fn: (f: Rule) => void) => () => {
    const f = finalizeDraft(d);
    fn(f);
    ctx.bump();
    onSaved(f.ruleId);
  };
  const doSave = finalizeAnd((f) => api.updateRule(meta.id, f));
  const doReview = finalizeAnd((f) => api.submitReview(meta.id, f, ctx.author));
  const doPublish = finalizeAnd((f) => api.publishRule(meta.id, f, meta.wasPublished, ctx.author));
  const doDelete = () => {
    api.deleteRule(meta.id);
    ctx.bump();
    onCancel(true);
  };

  const inputBox = { fontSize: 12.5 };
  // const local so the discriminated-union narrowing survives into the .map closures
  const cond = d.condition;

  return (
    <div className="fade-in cs-editor-grid">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '11px 14px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          <Icon name="shield" size={15} color="var(--garnet)" style={{ marginTop: 1 }} />
          <div className="metatext" style={{ color: 'var(--fg-2)' }}>
            <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>Written by a person, approved by a reviewer.</span> No AI
            writes or approves a rule.
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">The rule</span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <REField label="Title" wide>
                <input className="inp" style={inputBox} value={d.title} onChange={(e) => set({ title: e.target.value })} placeholder="Plain-language name for the rule" />
              </REField>
              <REField label="Rule reference" hint={meta.isNew ? 'editable while a draft' : 'permanent once published'}>
                <input
                  className="inp mono"
                  style={inputBox}
                  value={d.ruleId}
                  disabled={!meta.isNew}
                  onChange={(e) => set({ ruleId: e.target.value })}
                />
              </REField>
              <REField label="Returns">
                <select
                  className="inp"
                  style={inputBox}
                  value={d.outputType}
                  onChange={(e) => {
                    const t = e.target.value as StudioOutputType;
                    set({ outputType: t });
                    setOut({ type: t });
                  }}
                >
                  {OUTPUT_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </REField>
              <REField label="Jurisdiction">
                <select className="inp" style={inputBox} value={d.juris} onChange={(e) => set({ juris: e.target.value })}>
                  {api.jurisdictions().map((j) => (
                    <option key={j.code} value={j.code}>
                      {j.code} — {j.name}
                    </option>
                  ))}
                </select>
              </REField>
              <REField label="Domain">
                <select className="inp" style={inputBox} value={d.domain} onChange={(e) => set({ domain: e.target.value })}>
                  {api.domains().map((dm) => (
                    <option key={dm.key} value={dm.key}>
                      {dm.name}
                    </option>
                  ))}
                </select>
              </REField>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">When it applies</span>
            <div style={{ display: 'flex', gap: 2 }}>
              {(
                [
                  ['conditions', 'Conditions'],
                  ['table', 'Decision table'],
                ] as [Condition['kind'], string][]
              ).map(([k, lbl]) => (
                <button
                  key={k}
                  onClick={() => switchKind(k)}
                  className="mono"
                  style={{
                    padding: '4px 9px',
                    fontSize: 11,
                    borderRadius: 4,
                    color: cond.kind === k ? 'var(--garnet)' : 'var(--fg-3)',
                    background: cond.kind === k ? 'var(--garnet-muted)' : 'transparent',
                    border: '1px solid ' + (cond.kind === k ? 'var(--garnet)' : 'transparent'),
                  }}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <div className="label" style={{ marginBottom: 8 }}>
              What you check
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {cond.factors.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="inp"
                    style={{ flex: 1, fontSize: 12 }}
                    value={f.name}
                    onChange={(e) => setFactor(i, { name: e.target.value })}
                    placeholder="e.g. Active device"
                  />
                  <input
                    className="inp mono"
                    style={{ flex: 1, fontSize: 11.5 }}
                    value={f.note}
                    onChange={(e) => setFactor(i, { note: e.target.value })}
                    placeholder="e.g. yes / no"
                  />
                  <button className="btn btn-ghost btn-sm" onClick={() => delFactor(i)}>
                    <Icon name="x" size={13} />
                  </button>
                </div>
              ))}
              <button className="btn btn-sm" onClick={addFactor} style={{ alignSelf: 'flex-start' }}>
                <Icon name="plus" size={12} />
                Add factor
              </button>
            </div>

            {cond.kind === 'conditions' ? (
              <>
                <div className="label" style={{ marginBottom: 8 }}>
                  When this is true → this result
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cond.clauses.map((c, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 150px 28px', gap: 8, alignItems: 'start' }}>
                      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', paddingTop: 9 }}>
                        {i === 0 ? 'when' : 'or'}
                      </span>
                      <textarea
                        className="inp"
                        rows={2}
                        style={{ fontSize: 12 }}
                        value={c.when}
                        onChange={(e) => setClause(i, { when: e.target.value })}
                        placeholder="Describe the facts in plain language"
                      />
                      <input
                        className="inp"
                        style={{ fontSize: 12, color: 'var(--garnet)' }}
                        value={c.then}
                        onChange={(e) => setClause(i, { then: e.target.value })}
                        placeholder="Result"
                      />
                      <button className="btn btn-ghost btn-sm" onClick={() => delClause(i)} style={{ marginTop: 4 }}>
                        <Icon name="x" size={13} />
                      </button>
                    </div>
                  ))}
                  <button className="btn btn-sm" onClick={addClause} style={{ alignSelf: 'flex-start' }}>
                    <Icon name="plus" size={12} />
                    Add line
                  </button>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '24px 1fr',
                    gap: 8,
                    marginTop: 10,
                    padding: '10px 0 0',
                    borderTop: '1px dashed var(--abstain-border)',
                  }}
                >
                  <span className="mono" style={{ fontSize: 10, color: 'var(--abstain-text)', paddingTop: 2 }}>
                    else
                  </span>
                  <div>
                    <div className="mono" style={{ fontSize: 12.5, color: 'var(--abstain-text)' }}>
                      Otherwise → Abstain
                    </div>
                    <div className="metatext" style={{ marginTop: 3 }}>
                      Always here, can’t be removed. If nothing above matches, the rule abstains — it never guesses.
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="label" style={{ marginBottom: 8 }}>
                  Decision table
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 0, background: 'var(--bg-elevated)' }}>
                    {cond.columns.map((col, ci) => (
                      <input
                        key={ci}
                        className="inp mono"
                        value={col}
                        onChange={(e) => setColumn(ci, e.target.value)}
                        style={{
                          flex: 1,
                          borderRadius: 0,
                          border: 'none',
                          borderRight: ci < cond.columns.length - 1 ? '1px solid var(--border)' : 'none',
                          background: 'transparent',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      />
                    ))}
                    <span style={{ width: 28 }} />
                  </div>
                  {cond.rows.map((row, ri) => (
                    <div key={ri} style={{ display: 'flex', borderTop: '1px solid var(--border-subtle)' }}>
                      {row.map((cell, ci) => (
                        <input
                          key={ci}
                          className="inp mono"
                          value={cell}
                          onChange={(e) => setCell(ri, ci, e.target.value)}
                          style={{
                            flex: 1,
                            borderRadius: 0,
                            border: 'none',
                            borderRight: '1px solid var(--border-subtle)',
                            background: 'transparent',
                            fontSize: 11.5,
                            color: ci === row.length - 1 ? 'var(--garnet)' : 'var(--fg-1)',
                          }}
                        />
                      ))}
                      <button className="btn btn-ghost btn-sm" onClick={() => delRow(ri)} style={{ width: 28, borderRadius: 0 }}>
                        <Icon name="x" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <button className="btn btn-sm" onClick={addRow} style={{ alignSelf: 'flex-start', marginTop: 8 }}>
                  <Icon name="plus" size={12} />
                  Add row
                </button>
              </>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">{d.outputType === 'evidence' ? 'Document required' : 'What it returns'}</span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            {d.outputType === 'evidence' && (
              <div style={{ marginBottom: 12 }}>
                <REField label="Triggered by" hint="the document or finding you already have">
                  <input
                    className="inp"
                    style={inputBox}
                    value={d.output.trigger ?? ''}
                    onChange={(e) => setOut({ trigger: e.target.value })}
                    placeholder="e.g. Classification result, or a certificate on file"
                  />
                </REField>
              </div>
            )}
            <REField label={d.outputType === 'evidence' ? 'Document to require' : 'The result'}>
              <input
                className="inp"
                style={{ ...inputBox, color: 'var(--garnet)', fontFamily: 'var(--font-display)', fontSize: 16 }}
                value={d.output.value}
                onChange={(e) => setOut({ value: e.target.value })}
                placeholder={d.outputType === 'evidence' ? 'e.g. Notified Body certificate' : 'e.g. Class IIb'}
              />
            </REField>
            <div style={{ marginTop: 12 }}>
              <REField label="In plain words">
                <textarea
                  className="inp"
                  rows={2}
                  style={inputBox}
                  value={d.output.detail}
                  onChange={(e) => setOut({ detail: e.target.value })}
                  placeholder="One or two sentences a non-specialist can follow."
                />
              </REField>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Examples that must pass</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="metatext mono">
                {d.tests.filter((t) => t.status === 'pass').length}/{d.tests.length} checked
              </span>
              <button className="btn btn-sm mono" onClick={markChecked} disabled={!d.tests.length}>
                <Icon name="check" size={12} />
                Mark all checked
              </button>
            </div>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <p className="metatext" style={{ marginBottom: 10 }}>
              Worked examples that must pass. A rule can’t be published without them.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {d.tests.map((t, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 150px 70px 28px', gap: 8, alignItems: 'center' }}>
                  <input className="inp" style={{ fontSize: 12 }} value={t.input} onChange={(e) => setTest(i, { input: e.target.value })} placeholder="Example input" />
                  <input className="inp mono" style={{ fontSize: 11.5 }} value={t.expect} onChange={(e) => setTest(i, { expect: e.target.value })} placeholder="Expected" />
                  <button
                    onClick={() => setTest(i, { status: t.status === 'pass' ? 'pending' : 'pass' })}
                    className="badge"
                    style={{
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderColor: t.status === 'pass' ? 'var(--status-pass)' : 'var(--border)',
                      background: t.status === 'pass' ? 'var(--status-pass-bg)' : 'transparent',
                      color: t.status === 'pass' ? 'var(--status-pass-text)' : 'var(--fg-3)',
                    }}
                  >
                    {t.status === 'pass' ? 'checked' : 'pending'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => delTest(i)}>
                    <Icon name="x" size={13} />
                  </button>
                </div>
              ))}
              <button className="btn btn-sm" onClick={addTest} style={{ alignSelf: 'flex-start' }}>
                <Icon name="plus" size={12} />
                Add reference case
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Law it cites</span>
            <span className="label" style={{ color: 'var(--garnet)' }}>
              required
            </span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <CitationPicker ctx={ctx} citations={d.citations} setCitations={(c) => set({ citations: c })} />
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Dates</span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <REField label="From">
                <input
                  type="date"
                  className="inp mono"
                  style={{ fontSize: 12 }}
                  value={d.effectiveFrom || ''}
                  onChange={(e) => set({ effectiveFrom: e.target.value })}
                />
              </REField>
              <REField label="To (blank = current)">
                <input
                  type="date"
                  className="inp mono"
                  style={{ fontSize: 12 }}
                  value={d.effectiveTo ?? ''}
                  onChange={(e) => set({ effectiveTo: e.target.value || null })}
                />
              </REField>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Notes</span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <textarea
              className="inp"
              rows={4}
              style={{ fontSize: 12 }}
              value={d.rationale}
              onChange={(e) => set({ rationale: e.target.value })}
              placeholder="Why this rule reads the way it does."
            />
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Status</span>
          </div>
          <div className="panel-body" style={{ padding: 'var(--pad-x)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name={cited ? 'check' : 'alert'} size={13} color={cited ? 'var(--status-pass)' : 'var(--status-fail)'} />
                <span className="metatext" style={{ color: cited ? 'var(--fg-2)' : 'var(--status-fail-text)' }}>
                  {cited ? 'Cites a source of law' : 'Cite a source of law'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name={testsPass ? 'check' : 'alert'} size={13} color={testsPass ? 'var(--status-pass)' : 'var(--status-warn)'} />
                <span className="metatext" style={{ color: testsPass ? 'var(--fg-2)' : 'var(--status-warn-text)' }}>
                  {testsPass ? 'Has a checked example' : 'Add a checked example'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name={canPublishRole ? 'check' : 'alert'} size={13} color={canPublishRole ? 'var(--status-pass)' : 'var(--status-info)'} />
                <span className="metatext" style={{ color: canPublishRole ? 'var(--fg-2)' : 'var(--status-info-text)' }}>
                  {canPublishRole ? 'You can approve & publish' : 'A reviewer approves & publishes'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {canPublishRole ? (
                <button
                  className="btn btn-primary mono"
                  disabled={!canPublish}
                  onClick={doPublish}
                  title={meetsBar ? 'Approve and publish' : 'Blocked until cited and checked'}
                >
                  <Icon name="check" size={13} />
                  Approve & publish
                </button>
              ) : (
                <button className="btn btn-primary mono" disabled={!meetsBar} onClick={doReview} title="Send to a reviewer for approval">
                  <Icon name="arrow" size={13} />
                  Send for review
                </button>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn mono" style={{ flex: 1 }} onClick={doSave}>
                  Save
                </button>
                {canPublishRole && (
                  <button className="btn mono" style={{ flex: 1 }} onClick={doReview} disabled={!cited}>
                    Send for review
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm mono" style={{ flex: 1 }} onClick={() => onCancel(false)}>
                  Cancel
                </button>
                {(meta.isNew || d.status !== 'published') && (
                  <button className="btn btn-danger btn-sm mono" style={{ flex: 1 }} onClick={doDelete}>
                    Delete draft
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
